import OktaAuth, { CustomUserClaims, UserClaims } from '@okta/okta-auth-js';
import { Workspace } from '@tecton/ComponentRedesign';
import { uniq } from 'lodash';
import { createContext, ReactNode, useCallback, useContext, useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router';
import { useAuth } from './AuthContext';
import { useGetAuthorizedResources, useGetIsAuthorize } from '../../api/acl';
import { useGetGlobalsForWebUi } from '../../api/user';
import { useListWorkspaces } from '../../api/workspaces';
import { PrincipalType } from '../../types/tecton_proto/auth/principal';
import { ResourceType } from '../../types/tecton_proto/auth/resource';
import { ComputeIdentity } from '../../types/tecton_proto/common/compute_identity';
import WorkspaceUtils from '../../utils/workspace-utils';
import localStorageUtils, { STORAGE_KEYS } from '../../utils/localStorageUtils';

// FEATURE FLAGS AVAILABLE IN UI
export enum FeatureFlags {
  INTERNAL_DEBUG_SHOW_CONSUMPTION_INFO = 'INTERNAL_DEBUG_SHOW_CONSUMPTION_INFO',
  SHOW_NEW_FCO_LISTS = 'SHOW_NEW_FCO_LISTS',
  SHOW_NEW_UI_FV_DETAILS = 'SHOW_NEW_UI_FV_DETAILS',
  SHOW_NEW_TOP_NAV = 'SHOW_NEW_TOP_NAV',
  SHOW_NEW_FEATURE_SERVICES = 'SHOW_NEW_FEATURE_SERVICES',
  SHOW_NEW_WORKSPACE_HOME = 'SHOW_NEW_WORKSPACE_HOME',
  SHOW_JOB_DETAIL_EXTERNAL_URL = 'SHOW_JOB_DETAIL_EXTERNAL_URL',
  SHOW_JOB_LOGS = 'SHOW_JOB_LOGS',
  JOBS_GQL = 'JOBS_GQL',
  FCOS_LEVEL_DATAFLOW_DIAGRAMS = 'FCOS_LEVEL_DATAFLOW_DIAGRAMS',
  USE_UI_THEME = 'USE_UI_THEME',
  SHOW_RIFT_METRICS = 'SHOW_RIFT_METRICS',
  SHOW_SERVER_GROUPS = 'SHOW_SERVER_GROUPS',
}

// CURRENT VERSION OF FLAG
export enum FeatureFlagVersion {
  USE_UI_THEME = 'USE_UI_THEME',
  // HIDDEN
  INTERNAL_DEBUG_SHOW_CONSUMPTION_INFO = 'INTERNAL_DEBUG_SHOW_CONSUMPTION_INFO',
  SHOW_JOB_LOGS = 'SHOW_JOB_LOGS',
  SHOW_JOB_DETAIL_EXTERNAL_URL = 'SHOW_JOB_DETAIL_EXTERNAL_URL',
  //TEST
  FCOS_LEVEL_DATAFLOW_DIAGRAMS = 'FCOS_LEVEL_DATAFLOW_DIAGRAMS_TEST',
  SHOW_NEW_TOP_NAV = 'SHOW_NEW_TOP_NAV_GA',
  SHOW_NEW_WORKSPACE_HOME = 'SHOW_NEW_WORKSPACE_HOME_GA',
  JOBS_GQL = 'JOBS_GQL_GA',
  SHOW_NEW_FEATURE_SERVICES = 'SHOW_NEW_FEATURE_SERVICES_GA',
  SHOW_NEW_FCO_LISTS = 'SHOW_NEW_FCO_LISTS_GA',
  SHOW_NEW_UI_FV_DETAILS = 'SHOW_NEW_UI_FV_DETAILS_GA',
  SHOW_RIFT_METRICS = 'SHOW_RIFT_METRICS',
  SHOW_SERVER_GROUPS = 'SHOW_SERVER_GROUPS_GA',
}

type FeatureFlagDetail = {
  name: FeatureFlags;
  id: FeatureFlagVersion;
};

type FeatureFlagMapProps = {
  [key in FeatureFlags]: FeatureFlagDetail;
};

export interface Identity extends ComputeIdentity {
  name: string;
  id: string;
  isDefault: boolean;
}

export interface WorkspaceWithPermissions {
  name: string;
  permissions: string[];
  isAccessible: boolean;
  capabilities: { [key: string]: boolean };
  workspaceType: string;
  computeIdentities: Identity[];
}

// Feature Flag Map generated from enums above.
const generateFeatureFlagMap = (): FeatureFlagMapProps => {
  return Object.keys(FeatureFlags).reduce((acc, key) => {
    const flagKey = key as keyof typeof FeatureFlags;
    const version = FeatureFlagVersion[flagKey];
    acc[FeatureFlags[flagKey]] = {
      name: FeatureFlags[flagKey],
      id: version,
    };
    return acc;
  }, {} as FeatureFlagMapProps);
};

export const featureFlagMap = generateFeatureFlagMap();

const transformIdentities = (compute_identities: ComputeIdentity[]): Identity[] => {
  return (
    compute_identities?.map((identity, index) => {
      const id = identity?.databricks_service_principal?.application_id ?? '';
      return {
        name: 'Databricks Service Principal', // For future use when we have more than databricks as an identity provider.
        id,
        isDefault: index === 0, // The first one is always default.
      };
    }) ?? []
  );
};

export const HiddenModalFlags = new Set([
  FeatureFlags.JOBS_GQL,
  FeatureFlags.INTERNAL_DEBUG_SHOW_CONSUMPTION_INFO,
  FeatureFlags.SHOW_JOB_LOGS,
  FeatureFlags.SHOW_JOB_DETAIL_EXTERNAL_URL,
  FeatureFlags.FCOS_LEVEL_DATAFLOW_DIAGRAMS,
  FeatureFlags.SHOW_NEW_TOP_NAV,
  FeatureFlags.SHOW_NEW_WORKSPACE_HOME,
  FeatureFlags.JOBS_GQL,
  FeatureFlags.SHOW_NEW_FEATURE_SERVICES,
  FeatureFlags.SHOW_NEW_FCO_LISTS,
  FeatureFlags.SHOW_NEW_UI_FV_DETAILS,
  FeatureFlags.USE_UI_THEME,
]);

export const getWorkspaceDetails = (workspace?: WorkspaceWithPermissions): Workspace => {
  return {
    name: workspace?.name ?? '',
    workspaceType: workspace?.capabilities?.materializable ? 'Live' : 'Development',
    isAccessible: workspace?.isAccessible ?? false,
  };
};

export interface UserSettingsProps {
  workspace?: string;
  workspaceDetails?: Workspace;
  hasWorkspaceManagerAccess?: boolean;
  isAdmin?: boolean;
  hasOrganizationListAccess?: boolean;
  canUpdateMaterialization?: boolean;
  allWorkspaces?: WorkspaceWithPermissions[];
  hasMadeTheCallToGetAllWorkspaces?: boolean;
  hasAccessToProdWorkspace?: boolean;
  actionableWorkspaces?: string[]; //list of workspaces to which the user has operator access to.
  featureEnabled: (feature: FeatureFlags) => boolean;
  updateWorkspace: (workspace: string) => void;
  user?: UserClaims<CustomUserClaims> | undefined;
  isPublicFreeTrial: boolean;
  isStreamingIngestEnabled: boolean;
  isFeatureServerCachingEnabled: boolean;
  hasNoWorkspaceAssigned: boolean;
  canShowMainApplication: boolean;
  canShowComputeIdentity: boolean;
  oktaAuth?: OktaAuth; // need for logout. It's kind of like their context
  uiTheme?: 'light' | 'dark';
  setUiTheme?: (theme: 'light' | 'dark') => void;
  isLoadingWorkspaces: boolean;
}

enum WORKSPACE_PRINCIPAL_ACTION {
  // TODO We need to add all the actions from current.zed file
  WORKSPACE_MANAGER_ACCESS = 'manage_access',
  WORKSPACE_UPDATE_MATERIALIZATION_STATE = 'update_materialization_state',
}

enum ORGANIZATION_PRINCIPAL_ACTION {
  IS_ADMIN_ACCESS = 'admin',
  LIST_ACCESS = 'list_access',
}

export interface UserSettingsContextProps {
  children: ReactNode;
}

export const UserSettingsContext = createContext<UserSettingsProps | undefined>(undefined);

export const UserSettingsContextProvider = ({ children }: UserSettingsContextProps) => {
  const { workspace: workspaceParams } = useParams();

  const { user, oktaAuth, isTokenReady } = useAuth();

  const isOktaAuthReady = isTokenReady;

  const { data: permissionsData } = useGetAuthorizedResources({
    resource_type: ResourceType.RESOURCE_TYPE_WORKSPACE,
    action: 'read_config',
    principal_type: PrincipalType.PRINCIPAL_TYPE_USER,
    principal_id: isOktaAuthReady ? user?.sub : undefined,
  });

  const { data: ownerPermissionData } = useGetAuthorizedResources({
    resource_type: ResourceType.RESOURCE_TYPE_WORKSPACE,
    action: 'manage_role',
    principal_type: PrincipalType.PRINCIPAL_TYPE_USER,
    principal_id: isOktaAuthReady ? user?.sub : undefined,
  });

  const { data: editorPermissionData } = useGetAuthorizedResources({
    resource_type: ResourceType.RESOURCE_TYPE_WORKSPACE,
    action: 'apply_role',
    principal_type: PrincipalType.PRINCIPAL_TYPE_USER,
    principal_id: isOktaAuthReady ? user?.sub : undefined,
  });

  const { data: operatorPermissionData } = useGetAuthorizedResources({
    resource_type: ResourceType.RESOURCE_TYPE_WORKSPACE,
    action: 'operator',
    principal_type: PrincipalType.PRINCIPAL_TYPE_USER,
    principal_id: isOktaAuthReady ? user?.sub : undefined,
  });

  const organizationPermissions = Object.values(ORGANIZATION_PRINCIPAL_ACTION).map((action) => {
    const resource_type = ResourceType.RESOURCE_TYPE_ORGANIZATION;
    return {
      resource_type,
      action,
    };
  });

  const isOrganizationManageAccessPayload = {
    principal_type: PrincipalType.PRINCIPAL_TYPE_USER,
    principal_id: user?.sub ?? '',
    permissions: organizationPermissions,
  };

  const { data: getOrganizationIsAuthorizedData } = useGetIsAuthorize(
    isOktaAuthReady,
    isOrganizationManageAccessPayload
  );

  // For workspace loading, we only need workspace-level permissions, not organization permissions
  const isAuthorizationComplete =
    isOktaAuthReady &&
    permissionsData !== undefined &&
    ownerPermissionData !== undefined &&
    editorPermissionData !== undefined &&
    operatorPermissionData !== undefined;

  const mostRecentlyViewedWorkspace = localStorageUtils.getItem(STORAGE_KEYS.MOST_RECENTLY_VIEWED_WORKSPACE);
  const [workspace, setWorkspace] = useState<string | undefined>(mostRecentlyViewedWorkspace ?? undefined);
  const [workspaceDetails, setWorkspaceDetails] = useState<Workspace | undefined>();
  const hasSelectedWorkspace = useRef(false);

  const {
    data: allWorkspacesData,
    isSuccess: isSuccessAllWorkspaces,
    isLoading: isLoadingWorkspacesQuery,
  } = useListWorkspaces({
    enabled: isAuthorizationComplete,
  });

  // We're loading workspaces if either authorization isn't complete yet OR the workspace query is running
  const isLoadingWorkspaces = !isAuthorizationComplete || isLoadingWorkspacesQuery;
  const { data, isError } = useGetGlobalsForWebUi(isAuthorizationComplete);

  const ownerWorkspaces =
    ownerPermissionData?.authorized_resources?.map((authorizedResources) => {
      return authorizedResources?.resource_id ?? '';
    }) ?? [];

  const editorWorkspaces =
    editorPermissionData?.authorized_resources?.map((authorizedResources) => {
      return authorizedResources?.resource_id ?? '';
    }) ?? [];

  const operatorWorkspaces =
    operatorPermissionData?.authorized_resources?.map((authorizedResources) => {
      return authorizedResources?.resource_id ?? '';
    }) ?? [];

  // merge all the workspaces the user has access via owner, editor, and operator across all workspaces. This is currently used in jobs.
  const actionableWorkspaces = uniq([...ownerWorkspaces, ...editorWorkspaces, ...operatorWorkspaces]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const allWorkspaces: WorkspaceWithPermissions[] =
    allWorkspacesData?.workspaces?.map((apiWorkspace) => {
      const workspaceName = apiWorkspace.name ?? '';
      const checkPermissions =
        permissionsData?.authorized_resources?.filter((userWorkspace) => {
          return userWorkspace.resource_id === workspaceName;
        }) ?? [];

      const isLive = apiWorkspace.capabilities?.materializable === true;

      const capabilities: { [key: string]: boolean } = {
        materializable: apiWorkspace.capabilities?.materializable ?? false,
        offline_store_subdirectory_enabled: apiWorkspace.capabilities?.offline_store_subdirectory_enabled ?? false,
      };

      return {
        name: workspaceName,
        workspaceType: isLive ? 'Live' : 'Development',
        capabilities,
        isAccessible: checkPermissions.length === 1,
        permissions: checkPermissions.length === 1 ? checkPermissions[0].actions ?? [] : [],
        computeIdentities: transformIdentities(apiWorkspace?.compute_identities ?? []),
      };
    }) ?? [];

  const hasAccessToProdWorkspace = !!allWorkspaces?.find((ws) => {
    return ws.name === 'prod' && ws.isAccessible;
  });

  // Intelligent workspace selection when workspace data loads
  useEffect(() => {
    if (isSuccessAllWorkspaces && allWorkspaces && allWorkspaces.length > 0 && !hasSelectedWorkspace.current) {
      const currentWorkspaceAccess = allWorkspaces.find((ws) => ws.name === workspace);

      // If current workspace is not accessible, find a better one
      if (!currentWorkspaceAccess?.isAccessible) {
        const accessibleWorkspaces = allWorkspaces.filter((ws) => ws.isAccessible);

        if (accessibleWorkspaces.length > 0) {
          let selectedWorkspace: string | undefined;

          // 1. Try most recently viewed workspace if accessible
          if (mostRecentlyViewedWorkspace) {
            const recentWorkspaceAccess = accessibleWorkspaces.find((ws) => ws.name === mostRecentlyViewedWorkspace);
            if (recentWorkspaceAccess) {
              selectedWorkspace = mostRecentlyViewedWorkspace;
            }
          }

          // 2. Use first available workspace
          if (!selectedWorkspace) {
            selectedWorkspace = accessibleWorkspaces[0].name;
          }

          if (selectedWorkspace && selectedWorkspace !== workspace) {
            hasSelectedWorkspace.current = true;
            setWorkspace(selectedWorkspace);
            localStorageUtils.setItem(STORAGE_KEYS.MOST_RECENTLY_VIEWED_WORKSPACE, selectedWorkspace);
          }
        }
      } else {
        // Current workspace is accessible, mark as selected
        hasSelectedWorkspace.current = true;
      }
    }
  }, [isSuccessAllWorkspaces, allWorkspaces, mostRecentlyViewedWorkspace, workspace]);

  if (workspace) {
    const wsDetails = allWorkspaces?.find((ws) => ws.name === workspace);

    if (isSuccessAllWorkspaces || workspace !== workspaceDetails?.name) {
      if (!workspaceDetails && wsDetails) {
        setWorkspaceDetails(getWorkspaceDetails(wsDetails));
      }
    }
  }

  const hasNoWorkspaceAssigned = allWorkspaces?.filter((workspace: any) => workspace.isAccessible).length === 0;

  // MDS will send feature flag id
  // local storage will save feature flag id
  // we will use feature flag name to reference the flag in the app
  const featureEnabled = (feature: FeatureFlags) => {
    if (isError) return false;
    const flagId = featureFlagMap[feature].id;
    const storedFlag = localStorageUtils.getItem(flagId);
    if (storedFlag) {
      // If we find something in local storage, let's use it.
      return storedFlag?.toLowerCase() === 'true';
    }
    // next we look for flag in the payload
    return data?.key_values?.[flagId] === 'true';
  };

  const storedUiTheme = localStorageUtils.getItem(STORAGE_KEYS.UI_THEME);
  let uiTheme: 'light' | 'dark';
  if (storedUiTheme === 'light' || storedUiTheme === 'dark') {
    uiTheme = storedUiTheme;
  } else {
    uiTheme = 'light'; // default to light
  }

  const setUiTheme = (theme: 'light' | 'dark') => {
    localStorageUtils.setItem(STORAGE_KEYS.UI_THEME, theme);
    window.location.reload();
  };

  const updateWorkspace = useCallback(
    (ws: string) => {
      if (workspace !== ws) {
        setWorkspace(ws);
        const wsDetails = allWorkspaces?.find((workspace) => workspace.name === ws);

        if (wsDetails) {
          // Let's make sure we find a workspace before we set it in localStorage.
          // User can get into a really bad state if the workspace doesn't exist
          // Note: These localStorage operations may fail, but we continue with in-memory state
          WorkspaceUtils.setWorkspaceForLocalStorage(ws);
          WorkspaceUtils.addWorkspaceToRecent(wsDetails.name);
        }

        if (workspaceDetails) {
          setWorkspaceDetails(getWorkspaceDetails(wsDetails));
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workspace, setWorkspace, allWorkspaces]
  );

  // Workspace Permissions
  // We need both the user and workspace to be available in order for us to make the call
  const isWorkspaceGetIsAuthorizedEnabled = isAuthorizationComplete && workspace !== undefined;
  const workspacePermissions = Object.values(WORKSPACE_PRINCIPAL_ACTION).map((action) => {
    const resource_type = ResourceType.RESOURCE_TYPE_WORKSPACE;
    const resource_id = workspace;

    return {
      resource_type,
      resource_id,
      action,
    };
  });

  const isWorkspaceManageAccessPayload = {
    principal_type: PrincipalType.PRINCIPAL_TYPE_USER,
    principal_id: user?.sub ?? '',
    permissions: workspacePermissions,
  };

  const { data: getWorkspaceIsAuthorizedData } = useGetIsAuthorize(
    isWorkspaceGetIsAuthorizedEnabled,
    isWorkspaceManageAccessPayload
  );

  const isPublicFreeTrial = data?.key_values?.[`PUBLIC_FREE_TRIAL`] === 'true';
  // Is the Streaming Ingest enabled at the cluster level?
  const isStreamingIngestEnabled = data?.key_values?.[`ENABLE_PUSH_API`] === 'true';

  const isFeatureServerCachingEnabled = data?.key_values?.[`FEATURE_SERVER_CACHING_ENABLED`] === 'true';

  const canShowComputeIdentity = !!(
    data?.key_values?.[`COMPUTE_ENGINES`]?.includes('Spark') && data?.key_values?.['SPARK_RUNTIME'] === 'DATABRICKS'
  );

  const workspaces = allWorkspaces ?? [];
  const workspaceToTry = workspace ?? workspaceParams ?? undefined;
  const containsWorkspaces = workspaces.length > 0;
  const foundWorkspaceAccess = workspaces?.find((ws: WorkspaceWithPermissions) => {
    return ws.name === workspaceToTry;
  });

  let canShowMainApplication = true;

  // Don't determine "no workspace assigned" while still loading data
  if (hasNoWorkspaceAssigned && !isLoadingWorkspaces) {
    // no assigned workspace (and we're done loading)
    canShowMainApplication = false;
  }

  if (workspace !== undefined && foundWorkspaceAccess?.isAccessible === false && containsWorkspaces) {
    // we found the workspace, but you don't have access to it.
    canShowMainApplication = false;
  }

  if (!hasNoWorkspaceAssigned && workspaceToTry === undefined && containsWorkspaces) {
    // could be your first time logging in and we couldn't find a workspace in your localStorage
    canShowMainApplication = false;
  }

  // Don't set canShowMainApplication = false if workspace is just still loading
  if (foundWorkspaceAccess === undefined && !isLoadingWorkspaces) {
    // no workspace found (and we're not still loading data)
    canShowMainApplication = false;
  }

  if (hasAccessToProdWorkspace) {
    canShowMainApplication = true;
  }

  // Define the context value.
  const contextValue: UserSettingsProps = {
    workspace,
    workspaceDetails,
    allWorkspaces,
    hasAccessToProdWorkspace,
    hasWorkspaceManagerAccess:
      getWorkspaceIsAuthorizedData?.includes(WORKSPACE_PRINCIPAL_ACTION.WORKSPACE_MANAGER_ACCESS) ?? false,
    isAdmin: getOrganizationIsAuthorizedData?.includes(ORGANIZATION_PRINCIPAL_ACTION.IS_ADMIN_ACCESS) ?? false,
    hasOrganizationListAccess:
      getOrganizationIsAuthorizedData?.includes(ORGANIZATION_PRINCIPAL_ACTION.LIST_ACCESS) ?? false,
    // Does the user have access to hit retry on a materialization job?
    canUpdateMaterialization:
      getWorkspaceIsAuthorizedData?.includes(WORKSPACE_PRINCIPAL_ACTION.WORKSPACE_UPDATE_MATERIALIZATION_STATE) ??
      false,
    actionableWorkspaces,
    hasMadeTheCallToGetAllWorkspaces: isSuccessAllWorkspaces,
    featureEnabled,
    updateWorkspace,
    canShowMainApplication,
    user: user || undefined,
    isPublicFreeTrial,
    isStreamingIngestEnabled,
    isFeatureServerCachingEnabled,
    hasNoWorkspaceAssigned,
    canShowComputeIdentity,
    oktaAuth: oktaAuth || undefined,
    uiTheme,
    setUiTheme,
    isLoadingWorkspaces,
  };

  return <UserSettingsContext.Provider value={contextValue}>{children}</UserSettingsContext.Provider>;
};

// Create a custom hook to easily access the UserSettingsContext.
export const useUserSettings = (): UserSettingsProps => {
  const context = useContext(UserSettingsContext);
  if (context === undefined) {
    throw new Error('useUserSettings must be used within a UserSettingsContextProvider');
  }
  return context;
};
